/**
 * Token Roadmap Page
 * AIDLC Administrative Backend
 *
 * Token-based roadmap dashboard showing budget allocation by MoSCoW priority,
 * a prioritized table of backlog items with running token totals and funding
 * status, summary stat cards, and a what-if budget scenario slider.
 *
 * Consumes GET /api/roadmap and POST /api/roadmap/scenario.
 *
 * Personas: Executive, Ops, Orchestrator
 */

import { useState, useEffect, useCallback } from 'react';
import { Card } from '../shared/Card';
import { LoadingSpinner } from '../shared/LoadingSpinner';
import { EmptyState } from '../shared/EmptyState';
import { fmtNum, fmtTokens } from '../../utils/formatters';
import type {
  RoadmapData,
  RoadmapItem,
  RoadmapSummary,
  RoadmapScenarioData,
} from '../../services/orchestrationService';
import {
  getRoadmap,
  getRoadmapScenario,
} from '../../services/orchestrationService';

/** Priority display config */
const priorityConfig: Record<string, { label: string; color: string; barColor: string }> = {
  critical: { label: 'CRITICAL', color: 'text-red-700', barColor: 'bg-red-500' },
  high: { label: 'HIGH', color: 'text-orange-700', barColor: 'bg-orange-500' },
  medium: { label: 'MEDIUM', color: 'text-blue-700', barColor: 'bg-blue-500' },
  low: { label: 'LOW', color: 'text-gray-600', barColor: 'bg-gray-400' },
};

/** Size badge config */
const sizeConfig: Record<string, { bg: string; text: string }> = {
  S: { bg: 'bg-green-50', text: 'text-green-700' },
  M: { bg: 'bg-blue-50', text: 'text-blue-700' },
  L: { bg: 'bg-orange-50', text: 'text-orange-700' },
  XL: { bg: 'bg-red-50', text: 'text-red-700' },
};


// ── Main Component ───────────────────────────────────────────────────

export function TokenRoadmapPage() {
  const [roadmap, setRoadmap] = useState<RoadmapData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // What-if scenario state
  const [scenarioBudget, setScenarioBudget] = useState<number | null>(null);
  const [scenarioData, setScenarioData] = useState<RoadmapScenarioData | null>(null);
  const [isLoadingScenario, setIsLoadingScenario] = useState(false);

  const loadRoadmap = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getRoadmap();
      setRoadmap(data);
      // Initialize scenario budget to total token estimate
      if (data.summary) {
        setScenarioBudget(data.summary.totalTokenEstimate);
      }
    } catch (err) {
      console.error('[TokenRoadmap] Failed to load:', err);
      setError('Failed to load roadmap data. Please check the backend connection.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoadmap();
  }, [loadRoadmap]);

  // The active items: scenario included items if available, otherwise base roadmap
  const activeItems = scenarioData ? scenarioData.included.items : (roadmap?.items || []);
  const activeSummary = roadmap?.summary || null;

  // Debounced scenario fetching
  const handleBudgetChange = useCallback(async (newBudget: number) => {
    setScenarioBudget(newBudget);
    if (!roadmap) return;

    // Only call API if budget actually changed from original
    if (newBudget === roadmap.summary.totalTokenEstimate) {
      setScenarioData(null);
      return;
    }

    setIsLoadingScenario(true);
    try {
      const data = await getRoadmapScenario(newBudget);
      setScenarioData(data);
    } catch (err) {
      console.error('[TokenRoadmap] Scenario failed:', err);
      // Don't clear scenario data on error, just stop loading
    } finally {
      setIsLoadingScenario(false);
    }
  }, [roadmap]);

  const resetScenario = useCallback(() => {
    if (roadmap) {
      setScenarioBudget(roadmap.summary.totalTokenEstimate);
      setScenarioData(null);
    }
  }, [roadmap]);

  if (isLoading) {
    return <LoadingSpinner message="Loading token roadmap..." />;
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-16">
        <div className="text-red-500 mb-4">
          <svg className="w-12 h-12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
            <path d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
        </div>
        <p className="text-gray-700 font-medium mb-2">Unable to Load Roadmap</p>
        <p className="text-sm text-gray-500 mb-4 max-w-md text-center">{error}</p>
        <button
          onClick={loadRoadmap}
          className="px-4 py-2 bg-ldaja-blue text-white text-sm rounded-lg hover:bg-blue-700 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!roadmap || !activeSummary) {
    return (
      <EmptyState
        title="No roadmap data"
        description="No backlog items with token estimates found. Estimate some backlog items to see the roadmap."
      />
    );
  }

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-ldaja-black">Token Roadmap</h1>
          <p className="text-sm text-gray-500 mt-1">
            Budget allocation by priority with funding status for all estimated backlog items.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {scenarioData && (
            <span className="text-xs text-amber-600 bg-amber-50 px-2 py-1 rounded font-medium">
              What-If Mode
            </span>
          )}
          <button
            onClick={() => { resetScenario(); loadRoadmap(); }}
            className="flex items-center gap-2 px-3 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Budget Overview Bar */}
      <Card title="Budget Allocation by Priority" subtitle="Priority distribution of total token estimate">
        <BudgetOverviewBar summary={activeSummary} />
      </Card>

      {/* Summary Stat Cards */}
      <SummaryCards summary={activeSummary} scenarioData={scenarioData} />

      {/* What-If Scenario Control */}
      <Card title="What-If Budget Scenario" subtitle="Adjust the budget to see which items fit">
        <WhatIfControl
          currentBudget={scenarioBudget || activeSummary.totalTokenEstimate}
          originalBudget={roadmap.summary.totalTokenEstimate}
          isLoading={isLoadingScenario}
          onChange={handleBudgetChange}
          onReset={resetScenario}
        />
      </Card>

      {/* Roadmap Table */}
      <Card
        title="Roadmap Items"
        subtitle={`${activeItems.length} items sorted by priority`}
      >
        <RoadmapTable items={activeItems} />
      </Card>
    </div>
  );
}

// ── Budget Overview Bar ──────────────────────────────────────────────

function BudgetOverviewBar({ summary }: { summary: RoadmapSummary }) {
  const total = summary.totalTokenEstimate || 1;
  const priorities = ['critical', 'high', 'medium', 'low']
    .filter((key) => summary.byPriority[key])
    .map((key) => ({ key, value: summary.byPriority[key]?.tokens || 0 }));

  // Add buffer (remaining unallocated)
  const allocated = priorities.reduce((sum, p) => sum + p.value, 0);
  const buffer = Math.max(0, total - allocated);

  return (
    <div>
      {/* Stacked horizontal bar */}
      <div className="h-10 rounded-lg overflow-hidden flex mb-4">
        {priorities.map(({ key, value }) => {
          const pct = (value / total) * 100;
          if (pct <= 0) return null;
          const cfg = priorityConfig[key];
          return (
            <div
              key={key}
              className={`${cfg.barColor} flex items-center justify-center transition-all relative group`}
              style={{ width: `${pct}%` }}
              title={`${cfg.label}: ${fmtTokens(value)} tokens (${pct.toFixed(1)}%)`}
            >
              {pct > 10 && (
                <span className="text-white text-xs font-medium">
                  {cfg.label} {pct.toFixed(0)}%
                </span>
              )}
            </div>
          );
        })}
        {buffer > 0 && (
          <div
            className="bg-yellow-400 flex items-center justify-center"
            style={{ width: `${(buffer / total) * 100}%` }}
            title={`Buffer: ${fmtTokens(buffer)} tokens (${((buffer / total) * 100).toFixed(1)}%)`}
          >
            {(buffer / total) * 100 > 10 && (
              <span className="text-white text-xs font-medium">
                Buffer {((buffer / total) * 100).toFixed(0)}%
              </span>
            )}
          </div>
        )}
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-4">
        {priorities.map(({ key, value }) => {
          const cfg = priorityConfig[key];
          const pct = (value / total) * 100;
          return (
            <div key={key} className="flex items-center gap-2">
              <span className={`w-3 h-3 rounded-sm shrink-0 ${cfg.barColor}`} />
              <span className="text-xs text-gray-600">
                <span className="font-medium">{cfg.label}</span>: {fmtTokens(value)} ({pct.toFixed(1)}%)
              </span>
            </div>
          );
        })}
        {buffer > 0 && (
          <div className="flex items-center gap-2">
            <span className="w-3 h-3 rounded-sm shrink-0 bg-yellow-400" />
            <span className="text-xs text-gray-600">
              <span className="font-medium">Buffer</span>: {fmtTokens(buffer)} ({((buffer / total) * 100).toFixed(1)}%)
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Summary Stat Cards ───────────────────────────────────────────────

function SummaryCards({ summary, scenarioData }: { summary: RoadmapSummary; scenarioData?: RoadmapScenarioData | null }) {
  const cards = [
    { label: 'Total Items', value: String(summary.totalItems), color: 'text-ldaja-blue' },
    { label: 'Estimated', value: String(summary.estimatedItems), color: 'text-green-600' },
    { label: 'Unestimated', value: String(summary.unestimatedItems), color: summary.unestimatedItems > 0 ? 'text-yellow-600' : 'text-gray-400' },
    { label: 'Total Tokens', value: fmtTokens(summary.totalTokenEstimate), color: 'text-gray-700' },
    { label: 'Est. Cost', value: `$${summary.totalCostEstimate.toFixed(2)}`, color: 'text-purple-600' },
  ];

  if (scenarioData) {
    cards.push(
      { label: 'Included (Scenario)', value: String(scenarioData.included.count), color: 'text-green-600' },
      { label: 'Excluded (Scenario)', value: String(scenarioData.excluded.count), color: 'text-red-600' },
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-5 gap-4">
      {cards.map((card) => (
        <div key={card.label} className="bg-white border border-gray-200 rounded-lg p-4">
          <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">{card.label}</p>
          <p className={`text-2xl font-bold mt-1 ${card.color}`}>{card.value}</p>
        </div>
      ))}
    </div>
  );
}

// ── What-If Budget Control ───────────────────────────────────────────

function WhatIfControl({
  currentBudget,
  originalBudget,
  isLoading,
  onChange,
  onReset,
}: {
  currentBudget: number;
  originalBudget: number;
  isLoading: boolean;
  onChange: (budget: number) => void;
  onReset: () => void;
}) {
  const [inputValue, setInputValue] = useState(String(currentBudget));
  const isModified = currentBudget !== originalBudget;

  // Slider range: 50% to 200% of original budget
  const minBudget = Math.max(0, Math.round(originalBudget * 0.5));
  const maxBudget = Math.round(originalBudget * 2);

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = parseInt(e.target.value);
    setInputValue(String(val));
    onChange(val);
  };

  const handleInputSubmit = () => {
    const val = parseInt(inputValue);
    if (!isNaN(val) && val >= 0) {
      onChange(val);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        {/* Slider */}
        <div className="flex-1">
          <input
            type="range"
            min={minBudget}
            max={maxBudget}
            step={Math.max(1, Math.round(originalBudget * 0.01))}
            value={currentBudget}
            onChange={handleSliderChange}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-ldaja-blue"
          />
          <div className="flex justify-between text-[10px] text-gray-400 mt-1">
            <span>{fmtTokens(minBudget)}</span>
            <span className="font-medium text-gray-500">{fmtTokens(currentBudget)}</span>
            <span>{fmtTokens(maxBudget)}</span>
          </div>
        </div>

        {/* Manual input */}
        <div className="flex items-center gap-2">
          <input
            type="number"
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onBlur={handleInputSubmit}
            onKeyDown={(e) => { if (e.key === 'Enter') handleInputSubmit(); }}
            className="w-28 px-2 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-ldaja-blue/30 tabular-nums"
          />
          <span className="text-xs text-gray-400">tokens</span>
        </div>

        {/* Reset */}
        {isModified && (
          <button
            onClick={onReset}
            className="px-3 py-1.5 text-xs font-medium text-gray-600 border border-gray-200 rounded-md hover:bg-gray-50 transition-colors"
          >
            Reset
          </button>
        )}

        {/* Loading indicator */}
        {isLoading && (
          <svg className="animate-spin w-4 h-4 text-ldaja-blue" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
          </svg>
        )}
      </div>

      {/* Scenario delta info */}
      {isModified && (
        <div className="text-xs text-amber-600 bg-amber-50 rounded-md px-3 py-2">
          Budget changed by {currentBudget > originalBudget ? '+' : ''}
          {fmtTokens(currentBudget - originalBudget)} tokens (
          {currentBudget > originalBudget ? '+' : ''}
          {(((currentBudget - originalBudget) / originalBudget) * 100).toFixed(1)}% from original {fmtTokens(originalBudget)})
        </div>
      )}
    </div>
  );
}

// ── Roadmap Table ────────────────────────────────────────────────────

function RoadmapTable({ items }: { items: RoadmapItem[] }) {
  if (items.length === 0) {
    return (
      <p className="text-sm text-gray-400 py-6 text-center">
        No estimated backlog items available for the roadmap.
      </p>
    );
  }

  return (
    <div className="overflow-x-auto -mx-4">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-gray-400 uppercase tracking-wide border-b border-gray-100">
            <th className="px-4 py-2 font-medium w-8">#</th>
            <th className="px-4 py-2 font-medium">Priority</th>
            <th className="px-4 py-2 font-medium">Title</th>
            <th className="px-4 py-2 font-medium">Size</th>
            <th className="px-4 py-2 font-medium text-right">Token Estimate</th>
            <th className="px-4 py-2 font-medium text-right">Est. Cost</th>
            <th className="px-4 py-2 font-medium">Status</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-50">
          {items.map((item, idx) => {
            const pCfg = priorityConfig[item.priority] || priorityConfig.medium;
            const sCfg = item.sizeCategory ? (sizeConfig[item.sizeCategory] || sizeConfig.M) : { bg: 'bg-gray-50', text: 'text-gray-500' };

            return (
              <tr
                key={item.id || idx}
                className="hover:bg-gray-50/50 transition-colors"
              >
                {/* Row number */}
                <td className="px-4 py-3 text-xs text-gray-400 tabular-nums">{idx + 1}</td>

                {/* Priority badge */}
                <td className="px-4 py-3">
                  <span className={`text-xs font-semibold ${pCfg.color}`}>
                    {pCfg.label}
                  </span>
                </td>

                {/* Title */}
                <td className="px-4 py-3">
                  <span className="font-medium text-gray-900">{item.title}</span>
                </td>

                {/* Size */}
                <td className="px-4 py-3">
                  <span className={`text-xs font-medium px-2 py-0.5 rounded ${sCfg.bg} ${sCfg.text}`}>
                    {item.sizeCategory || '---'}
                  </span>
                </td>

                {/* Token Estimate */}
                <td className="px-4 py-3 text-right text-gray-700 tabular-nums font-medium">
                  {fmtNum(item.tokenEstimate)}
                </td>

                {/* Estimated Cost */}
                <td className="px-4 py-3 text-right text-gray-700 tabular-nums font-medium">
                  ${item.estimatedCost.toFixed(2)}
                </td>

                {/* Item Status */}
                <td className="px-4 py-3">
                  <span className="text-xs font-medium px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                    {item.status}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
